import { Box, ThemeProvider } from '@mui/material';
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ptBR } from '@mui/x-date-pickers/locales';
import BackdropComponent from 'components/BackdropComponent';
import FadeInShell from 'components/FadeInShell';
import HeaderComponent from 'components/HeaderComponent';
import { CriticalOccurrenceProvider } from "context/CriticalOccurrenceProvider";
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useAppRouting } from 'hooks/useAppRouting';
import { useAxiosInterceptors } from 'hooks/useAxiosInterceptors';
import { useIsDesktopLayout } from 'hooks/useIsDesktopLayout';
import "leaflet/dist/leaflet.css";
import { AuthenticationProvider } from 'pages/AuthenticationPages/context/AuthenticationContextProvider';
import { Suspense, useContext, useMemo } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ResponsibleInvitePage from "pages/PublicPages/ResponsibleInvitePage";
import routes from "routes/routes";
import { filterRoutesByPlan, getFirstAllowedRoute, getRoutes, isDevelopmentAccountLicense } from "routes/routeUtils";
import { isSportsPlan } from 'services/DefaultService';
import DefaultLayout from './components/DefaultLayout';
import MapView from './components/MapComponent/MapView';
import SidebarComponent from './components/SidebarComponent';
import { AppContext } from './context/AppContextProvider';
import { SyncJobProvider } from './context/SyncJobContext';
import { buildTheme } from './styles/theme';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("pt-br");

export default function App() {
	const { theme: appThemeName, isRouteAllowed } = useContext(AppContext);
	const theme = useMemo(() => buildTheme(appThemeName), [appThemeName]);
	const { isLogged, isLoaded } = useAppRouting(routes);
	const isDesktopLayout = useIsDesktopLayout();
	const menuRoutes = useMemo(
		() => filterRoutesByPlan(routes, isDevelopmentAccountLicense(), isSportsPlan()),
		[isLogged],
	);
	const fallbackRoute = useMemo(
		() => getFirstAllowedRoute(routes, isRouteAllowed) ?? "/app/authentication/signIn",
		[isLogged],
	);
	const routeElements = useMemo(
		() => getRoutes(routes, isLogged, isRouteAllowed, fallbackRoute),
		[isLogged, fallbackRoute],
	);

	useAxiosInterceptors();

	const location = useLocation();
	const isPublicResponsibleInviteRoute = /^\/app\/convite-responsavel\//.test(location.pathname);

	// Página 100% pública (responsável sem login/token no app) — precisa renderizar
	// fora da árvore autenticada/ProtectedRoute e sem esperar isLoaded/isLogged,
	// senão fica preso no fallback de redirecionamento pro signIn.
	if (isPublicResponsibleInviteRoute) {
		return (
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<ResponsibleInvitePage />
			</ThemeProvider>
		);
	}

	if (!isLoaded) {
		return null;
	}

	return (
		<ThemeProvider theme={theme}>
			<AuthenticationProvider>
				<LocalizationProvider
					dateAdapter={AdapterDayjs}
					adapterLocale={'pt-br'}
					localeText={
						ptBR.components.MuiLocalizationProvider.defaultProps.localeText
					}
				>
					<CssBaseline />
					{isLogged ? (
						<CriticalOccurrenceProvider>
							<SyncJobProvider>
								<FadeInShell>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											height: '100%',
										}}
									>
										{!isDesktopLayout && (
											<HeaderComponent routes={menuRoutes} />
										)}
										<Box sx={{ display: 'flex', height: '100%' }}>
											{isDesktopLayout && (
												<SidebarComponent
													brandName="AlertAi"
													routes={menuRoutes}
												/>
											)}
										</Box>
										<DefaultLayout>
											<Suspense fallback={<BackdropComponent open setOpen={() => {}} />}>
												<Routes>{routeElements}</Routes>
											</Suspense>
										</DefaultLayout>
									</Box>
								</FadeInShell>
							</SyncJobProvider>
						</CriticalOccurrenceProvider>
					) : (
						<FadeInShell>
							<Suspense fallback={<BackdropComponent open setOpen={() => {}} />}>
								<Routes>
									{routeElements}
									<Route path="/map" element={<MapView selectedId={null} selectedIds={[]} onSelect={() => {}} />} />

									<Route
										path="*"
										element={<Navigate to="/app/authentication/signIn" />}
									/>
								</Routes>
							</Suspense>
						</FadeInShell>
					)}
				</LocalizationProvider>
			</AuthenticationProvider>
		</ThemeProvider>
	);
}
